import type { PostDoc } from '@talkofthetown/shared';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { callables, db } from '../firebase';
import { friendlyError } from '../lib/errors';
import { timeAgo } from '../lib/format';
import { config } from '../config';
import { Dialog } from './Dialog';
import { PromotionQueue } from './PromotionQueue';
import { useToast } from './Toasts';

interface Props {
  open: boolean;
  now: number;
  onClose: () => void;
  onOpenPost: (id: string) => void;
}

type Held = { id: string; post: PostDoc<Timestamp> };

/** Admin queue: paid promotions to approve, and posts held by image screening or reports. */
export function ReviewDialog({ open, now, onClose, onOpenPost }: Props) {
  const toast = useToast();
  const [items, setItems] = useState<Held[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getDocs(
      query(
        collection(db, 'posts'),
        where('status', '==', 'held'),
        orderBy('createdAt', 'desc'),
        limit(50),
      ),
    )
      .then((snap) => {
        if (!cancelled) {
          setItems(snap.docs.map((d) => ({ id: d.id, post: d.data() as PostDoc<Timestamp> })));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast(friendlyError(err));
        setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, toast]);

  async function act(id: string, action: 'restore' | 'hide') {
    setBusy(id);
    try {
      await callables.adminAction({ action, postId: id });
      toast(action === 'restore' ? 'Restored: it’s back on the boards' : 'Removed');
      setItems((list) => (list ?? []).filter((x) => x.id !== id));
    } catch (err) {
      toast(friendlyError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} labelledBy="review-title" className="review">
      <h2 id="review-title">Held for review</h2>
      {open && config.payments && <PromotionQueue now={now} onOpenPost={onOpenPost} />}
      <p className="muted">
        Posts flagged by image screening or reported by several people. Restore what’s fine, remove
        the rest.
      </p>
      {items === null ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p>Nothing waiting. 🎉</p>
      ) : (
        <ul className="review__list">
          {items.map(({ id, post }) => (
            <li key={id} className="review__item">
              <button
                type="button"
                className="linklike review__title"
                onClick={() => onOpenPost(id)}
              >
                {post.title}
              </button>
              <span className="muted review__meta">
                {post.authorName} · {timeAgo(post.createdAt.toMillis(), now)} ·{' '}
                {post.moderation === 'flagged' && post.reportCount < 3
                  ? 'flagged by image screening'
                  : `${post.reportCount} reports`}
              </span>
              <span className="review__actions">
                <button
                  type="button"
                  className="btn btn--small"
                  disabled={busy === id}
                  onClick={() => act(id, 'restore')}
                >
                  Restore
                </button>
                <button
                  type="button"
                  className="btn btn--small btn--danger"
                  disabled={busy === id}
                  onClick={() => act(id, 'hide')}
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
